import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { petAPI } from '../services/api';
import PetAvatar from './PetAvatar';
import toast from 'react-hot-toast';
import { 
  ArrowLeftIcon, 
  ArrowRightIcon, 
  HeartIcon,
  SparklesIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

const PetCreationWizard = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [petData, setPetData] = useState({
    petTypeId: '',
    name: '',
    breed: '',
    age: '',
    weight: ''
  });
  
  const queryClient = useQueryClient();
  const { data: petTypes } = useQuery('petTypes', petAPI.getPetTypes);

  const createPetMutation = useMutation(petAPI.createPet, {
    onSuccess: () => {
      queryClient.invalidateQueries('pets');
      toast.success('🎉 Welcome to the family, ' + petData.name + '!');
      onClose();
      resetWizard();
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to add pet');
    }
  });

  const resetWizard = () => {
    setCurrentStep(1);
    setPetData({
      petTypeId: '',
      name: '',
      breed: '',
      age: '',
      weight: ''
    });
  };

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    const submitData = {
      petTypeId: parseInt(petData.petTypeId),
      name: petData.name,
      breed: petData.breed && petData.breed.trim() ? petData.breed : null,
      age: petData.age && petData.age.toString().trim() ? parseInt(petData.age) : null,
      weight: petData.weight && petData.weight.toString().trim() ? parseFloat(petData.weight) : null
    };
    createPetMutation.mutate(submitData);
  };

  const updatePetData = (field, value) => {
    setPetData(prev => ({ ...prev, [field]: value }));
  };

  const getSelectedPetType = () => {
    return petTypes?.data?.find(type => type.id === parseInt(petData.petTypeId));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return petData.petTypeId && petData.petTypeId !== '';
      case 2: return petData.name && petData.name.trim();
      case 3: return true; // Breed is optional
      default: return false;
    }
  };

  if (!isOpen) return null;

  const petTypesData = petTypes?.data || [];
  const selectedPetType = getSelectedPetType();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[95vh] overflow-hidden relative">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-500 to-secondary-500 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <SparklesIcon className="h-8 w-8 animate-spin-slow" />
              <h2 className="text-2xl font-bold">Welcome a New Friend! 🐾</h2>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl"
            >
              ×
            </button>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex items-center justify-between text-sm mb-2">
              <span>Step {currentStep} of 3</span>
              <span>{Math.round((currentStep / 3) * 100)}% Complete</span>
            </div>
            <div className="bg-white bg-opacity-20 rounded-full h-2">
              <div 
                className="bg-white rounded-full h-2 transition-all duration-500 ease-out"
                style={{ width: `${(currentStep / 3) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 overflow-y-auto" style={{ minHeight: '500px', maxHeight: 'calc(95vh - 180px)' }}>
          {/* Step 1: Pet Type Selection */}
          {currentStep === 1 && (
            <div className="text-center space-y-8">
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  What kind of companion are you adding? 🤔
                </h3>
                <p className="text-gray-600 text-lg">
                  Choose the type of pet that's about to join your training journey!
                </p>
              </div>
              
              <div className="max-h-80 overflow-y-auto pr-2 pb-8">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pb-12">
                  {petTypesData.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => updatePetData('petTypeId', type.id.toString())}
                      className={`p-6 rounded-2xl border-2 transition-all duration-200 hover:scale-105 ${
                        parseInt(petData.petTypeId) === type.id
                          ? 'border-primary-500 bg-primary-50 shadow-lg'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <PetAvatar petType={type.name} size="xl" />
                      <p className="mt-3 font-medium text-gray-900">{type.name}</p>
                    </button>
                  ))}
                </div>
                {petTypesData.length > 6 && (
                  <div className="text-center mt-4 pb-8">
                    <p className="text-sm text-gray-500 flex items-center justify-center">
                      <span className="mr-2">Scroll to see more pet types</span>
                      <span className="animate-bounce">↓</span>
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Name */}
          {currentStep === 2 && (
            <div className="text-center space-y-8 pb-8">
              <div className="animate-bounce-slow">
                <PetAvatar petType={selectedPetType?.name} size="2xl" animated={true} />
              </div>
              
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  What's your {selectedPetType?.name.toLowerCase()}'s name? ✨
                </h3>
                <p className="text-gray-600 text-lg mb-8">
                  Give your furry friend the perfect name for their training adventure!
                </p>
                
                <div className="max-w-md mx-auto">
                  <input
                    type="text"
                    value={petData.name}
                    onChange={(e) => updatePetData('name', e.target.value)}
                    placeholder="Enter a wonderful name..."
                    className="w-full px-6 py-4 text-xl text-center border-2 border-gray-200 rounded-2xl focus:border-primary-500 focus:outline-none transition-colors"
                    maxLength={100}
                    autoFocus
                  />
                  {petData.name && (
                    <div className="mt-4 p-4 bg-green-50 rounded-xl">
                      <p className="text-green-800 font-medium">
                        🎉 "{petData.name}" is a beautiful name!
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Breed */}
          {currentStep === 3 && (
            <div className="text-center space-y-8 pb-8">
              <div className="animate-float">
                <PetAvatar petType={selectedPetType?.name} size="2xl" />
              </div>
              
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  What breed is {petData.name}? 🧬
                </h3>
                <p className="text-gray-600 text-lg mb-8">
                  This helps us customize training tips! (Optional - you can skip this)
                </p>
                
                <div className="max-w-md mx-auto">
                  <input
                    type="text"
                    value={petData.breed}
                    onChange={(e) => updatePetData('breed', e.target.value)}
                    placeholder="e.g., Golden Retriever, Mixed Breed..."
                    className="w-full px-6 py-4 text-xl text-center border-2 border-gray-200 rounded-2xl focus:border-primary-500 focus:outline-none transition-colors"
                    maxLength={100}
                  />
                  <p className="mt-3 text-sm text-gray-500">
                    💡 Don't know the exact breed? No problem! Just leave it blank.
                  </p>
                </div>
              </div>
            </div>
          )}


        </div>

        {/* Footer Navigation */}
        <div className="absolute bottom-0 left-0 right-0 bg-gray-50 px-8 py-6 flex items-center justify-between rounded-b-3xl border-t border-gray-200">
          <button
            onClick={handlePrev}
            disabled={currentStep === 1}
            className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all ${
              currentStep === 1
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-700 hover:bg-gray-200'
            }`}
          >
            <ArrowLeftIcon className="h-4 w-4" />
            <span>Previous</span>
          </button>

          <div className="flex space-x-2">
            {[1, 2, 3].map((step) => (
              <div
                key={step}
                className={`w-3 h-3 rounded-full transition-all ${
                  step === currentStep
                    ? 'bg-primary-500 scale-125'
                    : step < currentStep
                    ? 'bg-green-400'
                    : 'bg-gray-300'
                }`}
              />
            ))}
          </div>

          {currentStep < 3 ? (
            <button
              onClick={handleNext}
              disabled={!canProceed()}
              className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all ${
                canProceed()
                  ? 'bg-primary-500 text-white hover:bg-primary-600 hover:scale-105'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <span>Next</span>
              <ArrowRightIcon className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={createPetMutation.isLoading || !petData.name.trim()}
              className="flex items-center space-x-2 px-8 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-medium hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <HeartIcon className="h-5 w-5" />
              <span>
                {createPetMutation.isLoading ? 'Adding...' : 'Welcome ' + petData.name + '!'}
              </span>
              <CheckIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PetCreationWizard;